import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

type StepState = "done" | "current" | "upcoming"

interface StepperStep {
  label: string
  state: StepState
  number: number
}

/**
 * Full-funnel steps: Purchase (done) → one step per offer → Register. The step
 * at `currentIndex` (an offer index, or offers.length for Register) is current;
 * earlier ones are done. Drives a correct, numbered bar for 2–5 offers.
 */
function buildSteps(offerLabels: string[], currentIndex: number): StepperStep[] {
  const labels = ["Purchase", ...offerLabels, "Register"]
  return labels.map((label, i) => {
    const offerIndex = i - 1 // -1 = Purchase, 0..n-1 = offers, n = Register
    let state: StepState = "upcoming"
    if (i === 0 || offerIndex < currentIndex) state = "done"
    else if (offerIndex === currentIndex) state = "current"
    return { label, state, number: i + 1 }
  })
}

function StepIndicator({ step }: { step: StepperStep }) {
  if (step.state === "done") {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-main-purple">
        <Check className="size-3.5 text-white" strokeWidth={3} />
      </div>
    )
  }
  if (step.state === "current") {
    // Purple outer ring + a purple centre dot with a dark gap between them.
    return (
      <div className="flex size-6 items-center justify-center rounded-full border-2 border-main-purple">
        <span className="size-3 rounded-full bg-purple-light" />
      </div>
    )
  }
  return (
    <div className="flex size-6 items-center justify-center rounded-full bg-purple-light/10 text-[10px] font-medium text-purple-light">
      {step.number}
    </div>
  )
}

interface OfferStepperProps {
  offerLabels: string[]
  currentIndex: number
  className?: string
}

/** The funnel progress row (Purchase → offers → Register) without the header chrome. */
export function OfferStepper({ offerLabels, currentIndex, className }: OfferStepperProps) {
  const steps = buildSteps(offerLabels, currentIndex)

  return (
    <div data-slot="offer-stepper" className={cn("flex items-center p-2", className)}>
      {steps.map((step, i) => (
        <div key={step.label} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
          <div className="flex w-full items-center gap-1">
            <span className={cn("h-px flex-1", i === 0 ? "bg-transparent" : "bg-purple-light/15")} />
            <StepIndicator step={step} />
            <span
              className={cn(
                "h-px flex-1",
                i === steps.length - 1 ? "bg-transparent" : "bg-purple-light/15",
              )}
            />
          </div>
          <span
            className={cn(
              "text-center text-xs",
              step.state === "upcoming" ? "text-purple-light/40" : "text-purple-light",
            )}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  )
}
